/**
 * WebSocket Service for OpenAI Realtime API
 * 
 * Direct WebSocket connection to OpenAI using ephemeral tokens.
 * Replaces WebRTC to fix role-switching issues (40% → 0% failure).
 */

import { REALTIME_MODEL, TRANSCRIPTION_MODEL } from '../../config/realtime';
import {
  ConnectionStatus,
  ClientEvent,
  ServerEvent,
  RealtimeSessionConfig,
  RealtimeAudioOutput,
} from './RealtimeAPITypes';
import type { SessionTrackingCallbacks } from '../../types/callbacks';
import { logger } from '../../utils/logger';

export interface WebSocketServiceConfig {
  // OpenAI model to use
  model?: string;
  // Voice to use (goes into audio.output.voice in GA API)
  voice?: string;
  // Enable VAD (Voice Activity Detection) - uses semantic_vad by default
  enableVAD?: boolean;
  // VAD threshold (0.0 - 1.0)
  vadThreshold?: number;
}

export interface WebSocketServiceCallbacks {
  // Connection status changes
  onConnectionStatusChange?: (status: ConnectionStatus) => void;
  
  // Audio events
  onAudioDataReceived?: (pcm16Data: Uint8Array) => void;
  onAudioDone?: () => void;
  
  // Transcript events
  onTranscriptReceived?: (transcript: string, itemId: string) => void;
  
  // Speech detection events
  onUserSpeechStarted?: (itemId: string) => void;
  onUserSpeechStopped?: (itemId: string) => void;
  
  // AI response events (text)
  onAIResponseDelta?: (text: string) => void;
  onAIResponseDone?: (text: string) => void;
  
  // Function call events (Analysis Mode)
  onFunctionCall?: (callId: string, name: string, args: Record<string, any>) => void | Promise<void>;
  
  // Error events
  onError?: (error: Error) => void;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private connectionStatus: ConnectionStatus = 'disconnected';
  private callbacks: WebSocketServiceCallbacks = {};
  private config: WebSocketServiceConfig = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 1000; // ms
  private currentResponseText = '';
  private sessionUpdatePromise: Promise<void> | null = null;
  private sessionUpdateResolve: (() => void) | null = null;
  
  // 🔧 FIX: Track if current response includes audio to avoid duplicate deltas
  // When response.audio.delta arrives, we know to use audio_transcript instead of text
  // This prevents garbled text from processing both response.text.delta AND response.audio_transcript.delta
  private currentResponseHasAudio = false;
  // Guard: true once onAIResponseDone has been called for the current response.
  // Prevents duplicate finalization if both response.text.done and response.audio_transcript.done fire.
  private currentResponseDoneHandled = false;
  
  // 🔧 FIX: Queue of pending session update resolvers to handle concurrent updates
  // Each session.update gets a unique ID, and we resolve ALL pending promises when session.updated arrives
  // Stores both resolve and reject so we can reject on disconnect
  private pendingSessionUpdates: Map<number, { resolve: () => void; reject: (err: Error) => void }> = new Map();
  private sessionUpdateCounter = 0;

  // 🔧 FIX: Guard against concurrent commitAndCreateResponse calls.
  // Rapid PTT clicks can trigger multiple commits in parallel, causing OpenAI to
  // close the WebSocket with "response already in progress". This flag blocks any
  // second call until the current response cycle completes (response.done).
  private isResponseInProgress = false;

  // NOTE: pendingResponseAfterFunctionOutput was removed.
  // We tried two approaches that both failed:
  //
  // ❌ setTimeout(50ms): Brittle — arbitrary delay, fails on slow connections.
  //
  // ❌ Wait for conversation.item.added: The server emits response.done BEFORE
  //    conversation.item.added. By the time conversation.item.added arrived,
  //    the session was already closing (WebSocket code 1005). Sending response.create
  //    at that point was too late.
  //
  // ✅ CORRECT: Send response.create immediately after conversation.item.create
  //    (function_call_output). The OpenAI server processes client events in FIFO order —
  //    the response.create is queued behind the function_call_output and will only be
  //    processed after the item is in context. No callback or delay needed.
  
  // Throttle noisy disconnect/error logging to session-tracking.
  // Some networks (VPN/corporate proxy/AV) can cause rapid connect→close loops.
  private lastDisconnectLogSignature: string | null = null;
  private lastDisconnectLogAtMs = 0;
  private suppressedDisconnectLogs = 0;
  private lastWsErrorLogAtMs = 0;
  
  // 🔒 CRITICAL: Bound session tracking ID to prevent race conditions
  // This ensures error logs go to the correct session, not a new one
  private boundSessionTrackingId: string | undefined;
  
  // Session tracking callbacks for logging (optional - package works without them)
  private sessionTracking: SessionTrackingCallbacks | undefined;

  constructor(
    callbacks: WebSocketServiceCallbacks = {},
    sessionTrackingId?: string,
    sessionTracking?: SessionTrackingCallbacks
  ) {
    this.callbacks = callbacks;
    this.boundSessionTrackingId = sessionTrackingId;
    this.sessionTracking = sessionTracking;
  }

  /**
   * Connect to OpenAI Realtime API via WebSocket
   * 
   * @param ephemeralToken - Token from backend /api/realtime-ws/token
   * @param instructions - System instructions (scenario prompt)
   * @param config - Optional configuration
   */
  async connect(
    ephemeralToken: string,
    instructions: string,
    config: WebSocketServiceConfig = {}
  ): Promise<void> {
    if (this.ws && this.connectionStatus === 'connected') {
      logger.warn('[WebSocketService] Already connected');
      return;
    }

    this.config = config;
    this.updateConnectionStatus('connecting');

    const model = config.model || REALTIME_MODEL;
    
    // Browser WebSocket doesn't support headers in constructor
    // We need to pass the token in the URL as a query parameter
    const wsUrl = `wss://api.openai.com/v1/realtime?model=${model}`;

    logger.info('[WebSocketService] Connecting to OpenAI...');

    try {
      // Create WebSocket with ephemeral token in subprotocol
      // This is the browser-compatible way to pass authentication
      // Based on OpenAI Realtime API docs: https://platform.openai.com/docs/api-reference/realtime-client-events
      this.ws = new WebSocket(wsUrl, [
        'realtime',
        `openai-insecure-api-key.${ephemeralToken}`, // Ephemeral token in subprotocol
        // 'openai-beta.realtime-v1', — REMOVED for GA API (gpt-realtime-2)
      ]);

      this.setupEventHandlers();
      
      // Wait for connection to open
      await this.waitForConnection();

      // Send session.update with instructions (if provided)
      if (instructions && instructions.trim()) {
        // Create promise to wait for session.updated confirmation
        this.sessionUpdatePromise = new Promise((resolve) => {
          this.sessionUpdateResolve = resolve;
        });
        
        this.sendSessionUpdate(instructions);
        
        // Wait for session.updated confirmation (model has processed prompt)
        logger.debug('[WebSocketService] Waiting for session.updated...');
        await this.sessionUpdatePromise;
        logger.info('[WebSocketService] Session ready');
      } else {
        logger.warn('[WebSocketService] No instructions on connect - will be sent later');
      }

    } catch (err) {
      console.error('[WebSocketService] Connection error:', err);
      this.updateConnectionStatus('error');
      this.callbacks.onError?.(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  /**
   * Disconnect from OpenAI
   */
  disconnect(): void {
    logger.debug('[WebSocketService] Disconnecting...');
    
    // Reject all pending session update promises before closing
    this.rejectPendingSessionUpdates('WebSocket disconnected');
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.updateConnectionStatus('disconnected');
    this.reconnectAttempts = 0;
  }
  
  /**
   * Reject all pending session update promises
   * Called when connection is lost to prevent stale promises from resolving incorrectly
   */
  private rejectPendingSessionUpdates(reason: string): void {
    if (this.pendingSessionUpdates.size > 0) {
      logger.debug(`[WebSocketService] Rejecting ${this.pendingSessionUpdates.size} pending session updates: ${reason}`);
      const error = new Error(reason);
      this.pendingSessionUpdates.forEach(({ reject }) => reject(error));
      this.pendingSessionUpdates.clear();
    }
  }

  /**
   * Update session configuration (send session.update to OpenAI)
   * Returns a Promise that resolves when session.updated is received.
   *
   * GA API session.update structure (see RealtimeAPITypes.ts for the full Beta→GA map).
   * Caller is responsible for providing audio.input.format / audio.output.format /
   * output_modalities when needed — this method just forwards what it receives.
   */
  updateSessionConfig(sessionConfig: RealtimeSessionConfig): Promise<void> {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot update session - not connected');
      return Promise.resolve();
    }

    // Build session.update — always include type: 'realtime' (required by GA API)
    const updateConfig: RealtimeSessionConfig = {
      type: 'realtime',
    };

    if (sessionConfig.model) {
      updateConfig.model = sessionConfig.model;
    }

    if (sessionConfig.instructions) {
      updateConfig.instructions = sessionConfig.instructions;
    }

    if (sessionConfig.output_modalities) {
      updateConfig.output_modalities = sessionConfig.output_modalities;
    }

    // Audio config (input.format + input.transcription + input.turn_detection,
    // output.voice + output.format). All optional; whatever the caller supplies is forwarded.
    if (sessionConfig.audio) {
      updateConfig.audio = sessionConfig.audio;
    }

    if (sessionConfig.tools !== undefined) {
      updateConfig.tools = sessionConfig.tools;
    }

    if (sessionConfig.tool_choice !== undefined) {
      updateConfig.tool_choice = sessionConfig.tool_choice;
    }

    // 🔧 FIX: Use unique ID for each update to handle concurrent session.update calls
    // All pending updates are resolved when session.updated arrives (OpenAI batches updates)
    const updateId = ++this.sessionUpdateCounter;
    const updatePromise = new Promise<void>((resolve, reject) => {
      this.pendingSessionUpdates.set(updateId, { resolve, reject });
    });

    const event: ClientEvent = {
      type: 'session.update',
      session: updateConfig,
    };

    logger.debug(`[WebSocketService] session.update keys: ${Object.keys(updateConfig).join(', ')}`);
    this.ws.send(JSON.stringify(event));
    
    return updatePromise;
  }

  /**
   * Send audio data to OpenAI
   * 
   * @param pcm16Data - PCM16 audio data (16-bit, 24kHz, mono)
   */
  sendAudio(pcm16Data: Uint8Array): void {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot send audio - not connected');
      return;
    }

    // Convert PCM16 to base64
    const base64Audio = this.arrayBufferToBase64(pcm16Data.buffer as ArrayBuffer);

    const event: ClientEvent = {
      type: 'input_audio_buffer.append',
      audio: base64Audio,
    };

    this.ws.send(JSON.stringify(event));
  }
  
  /**
   * Clear OpenAI's audio buffer (used when detecting false positives)
   */
  clearAudioBuffer(): void {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot clear buffer - not connected');
      return;
    }
    
    const event: ClientEvent = {
      type: 'input_audio_buffer.clear'
    };
    
    this.ws.send(JSON.stringify(event));
    logger.debug('[WebSocketService] Audio buffer cleared');
  }

  /**
   * Send text message to OpenAI
   * 
   * @param text - Text message from user
   */
  sendText(text: string): void {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot send text - not connected');
      return;
    }

    logger.debug('[WebSocketService] Sending text message');

    // Create conversation item with text
    const createEvent: ClientEvent = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [
          {
            type: 'input_text',
            text: text,
          },
        ],
      },
    };

    this.ws.send(JSON.stringify(createEvent));

    // Request response. With no `response` field the model inherits the session's
    // output_modalities (set to ['audio'] in sendSessionUpdate).
    const responseEvent: ClientEvent = {
      type: 'response.create',
    };

    this.ws.send(JSON.stringify(responseEvent));
  }

  /**
   * Inject a context item into the conversation WITHOUT triggering a response.
   *
   * Unlike sendText(), this does NOT send response.create — the item simply sits in
   * the conversation so the model reads it when it generates its next (auto-VAD)
   * response. Used to re-prime per-turn instructions (the language/dialect reminder)
   * independently of whether the model called assert_identity that turn — the
   * documented residual cause of PT-BR drift under tool_choice:'required'.
   *
   * Fired on the user's speech-start edge so the item lands BEFORE the user's
   * transcribed turn and well before semantic_vad auto-creates the response (no race).
   */
  injectContextItem(text: string): void {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot inject context item - not connected');
      return;
    }
    const createEvent: ClientEvent = {
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text }],
      },
    };
    this.ws.send(JSON.stringify(createEvent));
  }

  /**
   * Send function call result back to OpenAI
   * 
   * @param callId - The call_id from the function call event
   * @param result - The result to send back (will be stringified if object)
   * @param autoRespond - Whether to send response.create after the result (default: true).
   *   Pass false for identity/priming tools like assert_identity where the model should
   *   continue naturally without an explicit response.create trigger.
   *   With tool_choice: 'required', sending response.create after assert_identity causes
   *   an infinite loop (model is forced to call a tool again before every response).
   * @param responseToolChoice - Optional tool_choice override for the response.create event.
   *   Use 'auto' for assert_identity: the explicit override stops the session-level
   *   'required' from forcing yet another tool call (no infinite loop), while still
   *   letting the model process the function output (e.g. language_reminder) AND act on
   *   the user's turn (e.g. enter_analysis_mode / change_language) in the same response.
   */
  sendFunctionResult(callId: string, result: string | object, autoRespond: boolean = true, responseToolChoice?: 'auto' | 'none' | 'required' | { type: 'function'; name: string }): void {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot send function result - not connected');
      return;
    }

    const resultString = typeof result === 'string' ? result : JSON.stringify(result);

    logger.debug(`[WebSocketService] Sending function result for ${callId} (autoRespond=${autoRespond}, responseToolChoice=${responseToolChoice})`);

    // Send function call output
    const outputEvent: ClientEvent = {
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: resultString,
      },
    };
    this.ws.send(JSON.stringify(outputEvent));

    // Request response to process the function result.
    // For assert_identity: pass tool_choice: 'auto' so the model processes the
    // function output (including the language_reminder dialect priming) and stays
    // free to act on the user's turn (enter_analysis_mode / change_language) in the
    // same response. The explicit override breaks the infinite loop that session-level
    // tool_choice: 'required' would otherwise create — without the blunt 'none' that
    // used to forbid ALL tools here and block Analysis Mode.
    //
    // Send response.create immediately after conversation.item.create (function_call_output).
    //    The OpenAI server processes client events in order — the response.create will be
    //    queued after the function_call_output and processed once the item is in context.
    //    Waiting for conversation.item.added causes the response.create to arrive AFTER
    //    response.done, which closes the session before we can request a new response.
    if (autoRespond) {
      const responseEvent: ClientEvent = responseToolChoice
        ? { type: 'response.create', response: { tool_choice: responseToolChoice } }
        : { type: 'response.create' };
      if (this.ws && this.connectionStatus === 'connected') {
        this.ws.send(JSON.stringify(responseEvent));
      }
    }
  }

  /**
   * Commit audio buffer and request response
   * Called when the user releases the PTT button.
   * 
   * 🔧 FIX: Guard against concurrent calls from rapid PTT clicks.
   * If a response is already in progress, the second commit is silently dropped.
   * OpenAI closes the WebSocket with an error if response.create is sent while
   * a response is already active ("response already in progress").
   * The flag is cleared when response.done arrives.
   */
  logDiagnostic(type: string, context?: Record<string, unknown>): void {
    if (this.boundSessionTrackingId && this.sessionTracking?.logError) {
      this.sessionTracking.logError({
        type,
        message: type,
        timestamp: Date.now(),
        context: { ...context, severity: 'info' },
      });
    }
  }

  async commitAndCreateResponse(): Promise<void> {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot commit - not connected');
      return;
    }

    if (this.isResponseInProgress) {
      logger.warn('[WebSocketService] commitAndCreateResponse ignored - response already in progress (rapid PTT click)');
      return;
    }

    this.isResponseInProgress = true;
    logger.debug('[WebSocketService] Committing audio buffer...');

    // 1. Commit input audio buffer
    const commitEvent: ClientEvent = {
      type: 'input_audio_buffer.commit',
    };
    this.ws.send(JSON.stringify(commitEvent));

    // 2. Wait 200ms for commit to process before requesting response.
    // The OpenAI Realtime API does not emit a deterministic "commit acknowledged"
    // event in PTT mode (turn_detection: null). The 200ms delay is the established
    // approach to ensure the commit is enqueued before response.create is sent.
    await new Promise(resolve => setTimeout(resolve, 200));

    // 3. Request response (only if still connected after the delay)
    if (this.ws && this.connectionStatus === 'connected') {
      const responseEvent: ClientEvent = {
        type: 'response.create',
      };
      this.ws.send(JSON.stringify(responseEvent));
    } else {
      // Connection dropped during the delay — reset flag so next connect works
      this.isResponseInProgress = false;
    }
  }

  /**
   * Interrupt avatar (cancel current response and clear audio buffer)
   * 
   * This was IMPOSSIBLE with WebRTC - now it's trivial!
   */
  interruptAvatar(): void {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot interrupt - not connected');
      return;
    }

    logger.debug('[WebSocketService] Interrupting avatar...');

    try {
      // Cancel current response
      const cancelEvent: ClientEvent = {
        type: 'response.cancel',
      };
      this.ws.send(JSON.stringify(cancelEvent));

      // Clear input audio buffer
      const clearEvent: ClientEvent = {
        type: 'input_audio_buffer.clear',
      };
      this.ws.send(JSON.stringify(clearEvent));
    } catch (error) {
      logger.debug('[WebSocketService] Avatar interruption failed (may not be needed)');
      // Don't throw - this is expected if no active response
    }

    // Trigger audio done callback
    this.callbacks.onAudioDone?.();
  }

  /**
   * Delete a conversation item from the server's context
   * Used for context truncation/summarization to prevent the context window from growing indefinitely.
   * 
   * @param itemId - The ID of the conversation item to delete
   */
  deleteConversationItem(itemId: string): void {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot delete item - not connected');
      return;
    }

    logger.debug(`[WebSocketService] Deleting conversation item: ${itemId}`);

    const event: ClientEvent = {
      type: 'conversation.item.delete',
      item_id: itemId,
    };

    this.ws.send(JSON.stringify(event));
  }

  /**
   * Clear input audio buffer to prepare for next speech segment
   *
   * This prevents consecutive speech from being transcribed as one long message
   * by clearing any remaining audio data between speech segments.
   */
  clearInputAudioBuffer(): void {
    if (!this.ws || this.connectionStatus !== 'connected') {
      logger.warn('[WebSocketService] Cannot clear audio buffer - not connected');
      return;
    }

    // Clear input audio buffer
    const clearEvent: ClientEvent = {
      type: 'input_audio_buffer.clear'
    };

    try {
      this.ws.send(JSON.stringify(clearEvent));
    } catch (error) {
      logger.error('[WebSocketService] Failed to clear audio buffer:', error);
    }
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      logger.info('[WebSocketService] Connected to OpenAI');
      this.updateConnectionStatus('connected');
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const serverEvent = JSON.parse(event.data) as ServerEvent;
        this.handleServerEvent(serverEvent);
      } catch (error) {
        logger.error('[WebSocketService] Error parsing message:', error);
      }
    };

    this.ws.onerror = (error) => {
      logger.error('[WebSocketService] WebSocket error:', error);
      this.updateConnectionStatus('error');
      
      // Log error to session tracking (if callbacks provided)
      // 🔒 Pass bound sessionTrackingId to prevent race conditions
      const now = Date.now();
      // Avoid spamming identical websocket_error logs in rapid failure loops.
      // Log at most once every 15 seconds.
      if (now - this.lastWsErrorLogAtMs >= 15000 && this.boundSessionTrackingId && this.sessionTracking?.logError) {
        this.lastWsErrorLogAtMs = now;
        this.sessionTracking.logError({
          type: 'websocket_error',
          message: 'WebSocket connection error',
          timestamp: now,
          context: { error: String(error) }
        });
      }
      
      this.callbacks.onError?.(new Error('WebSocket error'));
    };

    this.ws.onclose = (event) => {
      logger.info(`[WebSocketService] WebSocket closed: code=${event.code}, clean=${event.wasClean}`);

      this.updateConnectionStatus('disconnected');
      
      // Reject pending session updates to prevent stale promises from resolving on reconnect
      this.rejectPendingSessionUpdates(`WebSocket closed: ${event.reason || 'Unknown reason'}`);
      
      // Log unexpected disconnections to session tracking
      if (!event.wasClean) {
        const now = Date.now();
        const signature = `${event.code}|${event.reason || ''}|${event.wasClean ? 'clean' : 'unclean'}`;

        // Throttle repeated disconnects: log at most once every 15s per signature.
        // If we suppress some, include the suppressed count in the next emitted log.
        const shouldThrottle = (
          this.lastDisconnectLogSignature === signature &&
          (now - this.lastDisconnectLogAtMs) < 15000
        );

        if (shouldThrottle) {
          this.suppressedDisconnectLogs++;
        } else if (this.boundSessionTrackingId && this.sessionTracking?.logError) {
          const suppressed = this.suppressedDisconnectLogs;
          this.suppressedDisconnectLogs = 0;
          this.lastDisconnectLogSignature = signature;
          this.lastDisconnectLogAtMs = now;

          // 🔒 sessionTrackingId is REQUIRED - only log if available and callbacks provided
          this.sessionTracking.logError({
            type: 'websocket_disconnect',
            message: `WebSocket closed unexpectedly: ${event.reason || 'Unknown reason'}`,
            timestamp: now,
            code: String(event.code),
            context: {
              wasClean: event.wasClean,
              code: event.code,
              reason: event.reason,
              suppressedDuplicates: suppressed
            }
          });
        }
      }

      // Attempt reconnection if it wasn't a clean close
      if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.attemptReconnect();
      }
    };
  }

  private handleServerEvent(event: ServerEvent): void {
    // Only log non-frequent events in development
    // 🚨 GA API: audio events renamed to response.output_audio.* and response.output_audio_transcript.*
    const skipLog = [
      'response.audio.delta', 'response.audio_transcript.delta', 'response.text.delta',
      'response.output_audio.delta', 'response.output_audio_transcript.delta',
    ];
    if (!skipLog.includes(event.type)) {
      logger.debug(`[WebSocketService] Server event: ${event.type}`);
    }

    switch (event.type) {
      case 'session.created':
        logger.info('[WebSocketService] Session created');
        break;

      case 'session.updated':
        logger.debug('[WebSocketService] Session updated');
        // Resolve the session update promise (for connect() to proceed)
        if (this.sessionUpdateResolve) {
          this.sessionUpdateResolve();
          this.sessionUpdateResolve = null;
          this.sessionUpdatePromise = null;
        }
        // Resolve ALL pending session update promises
        // OpenAI batches updates, so one session.updated can correspond to multiple session.update calls
        if (this.pendingSessionUpdates.size > 0) {
          logger.debug(`[WebSocketService] Resolving ${this.pendingSessionUpdates.size} pending session updates`);
          this.pendingSessionUpdates.forEach(({ resolve }) => resolve());
          this.pendingSessionUpdates.clear();
        }
        break;

      case 'input_audio_buffer.speech_started':
        logger.debug('[WebSocketService] User started speaking');
        this.callbacks.onUserSpeechStarted?.(event.item_id);
        break;

      case 'input_audio_buffer.speech_stopped':
        logger.debug('[WebSocketService] User stopped speaking');
        this.callbacks.onUserSpeechStopped?.(event.item_id);
        // NOTE: With semantic_vad, OpenAI automatically commits and creates response
        // We don't need to call commitAndCreateResponse() manually - it would cause conflicts
        // Just notify the callback and let semantic_vad handle the rest
        break;

      case 'conversation.item.input_audio_transcription.completed':
        logger.debug(`[WebSocketService] User transcript: ${event.transcript}`);
        this.callbacks.onTranscriptReceived?.(event.transcript, event.item_id);
        break;

      // ======================================================================
      // 🚨 GA API (gpt-realtime-2): Audio delta events
      // Beta: response.audio.delta → GA: response.output_audio.delta
      // Both handled identically — fall through to shared logic
      // ======================================================================
      case 'response.output_audio.delta': // GA API
      case 'response.audio.delta': {      // Beta API (backwards compat)
        // 🔧 FIX: Mark that this response has audio - use audio_transcript for text, not response.text
        this.currentResponseHasAudio = true;
        // Reset done guard on first audio delta (new response starting)
        this.currentResponseDoneHandled = false;
        
        // Decode base64 PCM16 and pass to callback
        const pcm16Data = this.base64ToArrayBuffer(event.delta);
        this.callbacks.onAudioDataReceived?.(new Uint8Array(pcm16Data));
        break;
      }

      // ======================================================================
      // 🚨 GA API (gpt-realtime-2): Audio done events
      // Beta: response.audio.done → GA: response.output_audio.done
      // ======================================================================
      case 'response.output_audio.done': // GA API
      case 'response.audio.done':        // Beta API (backwards compat)
        logger.debug('[WebSocketService] AI audio done');
        this.callbacks.onAudioDone?.();
        // Note: Text is handled by response.output_audio_transcript.done, not here
        // Avoid double-calling onAIResponseDone
        break;

      case 'response.text.delta':
        // 🔧 FIX: Skip response.text.delta when response has audio
        // For audio responses, we use response.output_audio_transcript.delta instead
        // Processing both would cause duplicate/garbled text
        if (this.currentResponseHasAudio) {
          // Audio response - skip text.delta, use audio_transcript.delta instead
          break;
        }
        this.currentResponseText += event.delta;
        this.callbacks.onAIResponseDelta?.(event.delta);
        break;

      case 'response.text.done':
        // 🔧 FIX: Skip response.text.done when response has audio
        // For audio responses, we use response.output_audio_transcript.done instead
        if (this.currentResponseHasAudio) {
          // Audio response - skip text.done, use audio_transcript.done instead
          break;
        }
        // 🔧 FIX: Guard against calling onAIResponseDone twice per response
        if (this.currentResponseDoneHandled) {
          logger.debug('[WebSocketService] Skipping duplicate response.text.done (already handled)');
          break;
        }
        this.currentResponseDoneHandled = true;
        logger.debug(`[WebSocketService] AI text done: ${event.text?.substring(0, 50)}...`);
        this.callbacks.onAIResponseDone?.(event.text);
        this.currentResponseText = '';
        break;

      // ======================================================================
      // 🚨 GA API (gpt-realtime-2): Audio transcript delta events
      // Beta: response.audio_transcript.delta → GA: response.output_audio_transcript.delta
      // ======================================================================
      case 'response.output_audio_transcript.delta': // GA API
      case 'response.audio_transcript.delta':        // Beta API (backwards compat)
        // AI's speech-to-text of what it's saying (real-time)
        this.currentResponseText += event.delta;
        this.callbacks.onAIResponseDelta?.(event.delta);
        break;

      // ======================================================================
      // 🚨 GA API (gpt-realtime-2): Audio transcript done events
      // Beta: response.audio_transcript.done → GA: response.output_audio_transcript.done
      // ======================================================================
      case 'response.output_audio_transcript.done': // GA API
      case 'response.audio_transcript.done': {      // Beta API (backwards compat)
        // AI's complete transcript of what it said
        const audioTranscript = event.transcript;
        logger.debug(`[WebSocketService] AI audio transcript done: ${audioTranscript?.substring(0, 50)}...`);
        // 🔧 FIX: Guard against calling onAIResponseDone twice per response.
        // If response.text.done already called it (due to event ordering),
        // skip this to prevent duplicate messages in the transcript.
        if (this.currentResponseDoneHandled) {
          logger.debug('[WebSocketService] Skipping duplicate audio_transcript.done (already handled by text.done)');
        } else if (audioTranscript && audioTranscript.trim()) {
          this.currentResponseDoneHandled = true;
          this.callbacks.onAIResponseDone?.(audioTranscript);
        }
        this.currentResponseText = '';
        break;
      }

      case 'response.function_call_arguments.delta':
        // Function call arguments streaming
        break;

      case 'response.function_call_arguments.done':
        // Function call complete - parse arguments and notify callback
        logger.info(`[WebSocketService] Function call: ${(event as any).name}`);
        if (this.callbacks.onFunctionCall) {
          try {
            const args = JSON.parse((event as any).arguments || '{}');
            this.callbacks.onFunctionCall(
              (event as any).call_id,
              (event as any).name,
              args
            );
          } catch (parseError) {
            logger.error('[WebSocketService] Failed to parse function arguments:', parseError);
          }
        }
        break;

      case 'conversation.item.added':
        // Informational — item confirmed in context. No action needed.
        // response.create is sent immediately after conversation.item.create (FIFO order).
        break;

      case 'response.content_part.added':
      case 'response.content_part.done':
      case 'response.output_item.done':
      case 'conversation.item.done': // 🚨 GA API: new event — informational, no action needed
      case 'rate_limits.updated':
        // Informational events - don't log
        break;

      case 'response.done':
        logger.debug(`[WebSocketService] Response completed: ${event.status}`);
        // 🔧 FIX: Reset flags for next response
        this.currentResponseHasAudio = false;
        this.currentResponseDoneHandled = false;
        // 🔧 FIX: Allow next PTT commit now that the response cycle is complete
        this.isResponseInProgress = false;
        break;

      case 'error':
        // Handle expected cancellation errors gracefully
        if (event.error.code === 'response_cancel_not_active') {
          logger.debug('[WebSocketService] Cancellation not needed (no active response)');
          // Don't propagate this as a critical error - it's expected when interrupting completed responses
          break;
        }

        logger.error('[WebSocketService] OpenAI error:', event.error);
        this.callbacks.onError?.(new Error(event.error.message));
        break;

      default:
        // Log unknown events for debugging
        logger.debug(`[WebSocketService] Unknown event: ${(event as any).type}`);
    }
  }

  private sendSessionUpdate(instructions: string): void {
    if (!this.ws) return;

    logger.debug('[WebSocketService] Sending session.update', {
      instructionsLength: instructions.length,
    });

    // GA API session.update — see RealtimeAPITypes.ts header for the full Beta→GA map.
    // The fields below are the minimum needed at boot; tools/tool_choice/output_modalities
    // overrides are layered on by useSessionLifecycle's updateSession() call.
    //
    // audio.{input,output}.format MUST be sent explicitly. The Beta API accepted
    // input_audio_format/output_audio_format as strings; in GA they moved into the
    // audio subtree as objects, and omitting them lets the server fall back to a
    // default that does not always match our pipeline (we always send PCM16 mono @ 24 kHz).
    const sessionConfig: RealtimeSessionConfig = {
      type: 'realtime',
      model: this.config.model || REALTIME_MODEL,
      instructions: instructions,
      output_modalities: ['audio'],
      audio: {
        input: {
          format: { type: 'audio/pcm', rate: 24000 },
          transcription: {
            model: TRANSCRIPTION_MODEL,
          },
          // Turn detection: Start with null (disabled) by default.
          // In PTT mode, stays null (user controls start/stop).
          // In VAD mode, the session lifecycle will update to semantic_vad after greeting.
          turn_detection: null,
        },
        output: {
          voice: (this.config.voice as RealtimeAudioOutput['voice']) || 'alloy',
          format: { type: 'audio/pcm', rate: 24000 },
        },
      },
    };

    const event: ClientEvent = {
      type: 'session.update',
      session: sessionConfig,
    };

    logger.debug('[WebSocketService] Sending session.update (GA format)');
    this.ws.send(JSON.stringify(event));
  }

  private updateConnectionStatus(status: ConnectionStatus): void {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      logger.debug(`[WebSocketService] Connection status: ${status}`);
      this.callbacks.onConnectionStatusChange?.(status);
    }
  }

  private waitForConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.ws) {
        reject(new Error('WebSocket not initialized'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000); // 10s timeout

      this.ws.onopen = () => {
        clearTimeout(timeout);
        this.updateConnectionStatus('connected');
        this.reconnectAttempts = 0;
        resolve();
      };

      this.ws.onerror = (_error) => {
        clearTimeout(timeout);
        this.updateConnectionStatus('error');
        reject(new Error('WebSocket connection error'));
      };
    });
  }

  private attemptReconnect(): void {
    this.reconnectAttempts++;
    logger.info(`[WebSocketService] Reconnecting... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      // Note: We can't reconnect without a new token
      // The hook will need to handle reconnection by calling connect() again
      logger.debug('[WebSocketService] Reconnection requires new token - hook must handle this');
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // ============================================================================
  // Getters
  // ============================================================================

  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  isConnected(): boolean {
    return this.connectionStatus === 'connected';
  }
}

